import axios, { type AxiosError } from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry, { isNetworkError } from 'axios-retry';
import React from 'react';
import { API_URL } from './constants';
import { useAuthStore } from '../features/auth/store/authStore';
import { toast } from '@/hooks/use-toast';
import { capitalizeFirst } from './utils';
import { getApiErrorMessage, getApiErrorsList } from './api-utils';

// Extend axios config so callers can opt-out of the global 400 error toast
declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean;
  }
}

export const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000;
  },
  retryCondition: (error) => {
    const method = error.config?.method?.toLowerCase();
    const isIdempotent = method === 'get' || method === 'head' || method === 'options';
    return Boolean(
      isNetworkError(error) ||
      (error.response && error.response.status >= 500 && isIdempotent)
    );
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore.getState();
    const token = authStore.getStoredToken();
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Immediately redirect to login for invalid token code
    if (response.data?.response_schema?.response_code === '8002') {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(new Error('Invalid Token'));
    }
    return response;
  },
  async (error: AxiosError) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = error.response?.data as any;
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    const status = error.response?.status;
    const responseCode = responseData?.response_schema?.response_code;
    const responseMessage =
      typeof responseData?.response_schema?.response_message === 'string'
        ? responseData.response_schema.response_message
        : '';

    const isInvalidToken =
      responseCode === '8002' ||
      responseMessage.toLowerCase().includes('invalid token');

    const isExpiredToken =
      status === 401 ||
      responseCode === '8001' ||
      responseMessage.toLowerCase().includes('expired token');

    // If token is explicitly invalid, clear auth and redirect to login immediately
    if (isInvalidToken) {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 401 / Expired Token: attempt token refresh transparently without popup
    if (isExpiredToken) {
      // If original request already retried or has no refresh mechanism, logout
      if (originalRequest?._retry) {
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest?.headers) {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            }
            return Promise.reject(error);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      if (originalRequest) {
        originalRequest._retry = true;
      }
      isRefreshing = true;

      try {
        const authStore = useAuthStore.getState();
        const newToken = await authStore.refreshTokenIfNeeded();

        if (newToken) {
          processQueue(null, newToken);

          if (originalRequest?.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          }
          return axiosInstance(originalRequest);
        } else {
          processQueue(error, null);
          useAuthStore.getState().clearAuth();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Redirect to unauthorized page on 403 Forbidden
    if (status === 403) {
      window.location.href = '/unauthorized';
      return Promise.reject(error);
    }

    // Handle 400 Bad Request — show toast with API error messages
    if (status === 400) {
      // Skip if the individual call opts out (has its own error handling)
      if (!originalRequest?.skipGlobalErrorToast) {
        const errors = getApiErrorsList(error);
        const fallbackMessage: string =
          responseData?.response_schema?.response_message ?? 'Invalid request';
        const detailedMessage = getApiErrorMessage(error, '');
        const traceId: string | undefined = responseData?.trace_id;

        if (errors.length > 1) {
          toast({
            title: capitalizeFirst(fallbackMessage),
            description: React.createElement(
              'ul',
              { className: 'list-disc pl-4 space-y-1 mt-1' },
              ...errors.map((e, i) =>
                React.createElement(
                  'li',
                  { key: i },
                  capitalizeFirst(e.message),
                )
              ),
            ),
            traceId,
            variant: 'destructive',
          });
        } else if (errors.length === 1) {
          toast({
            title: capitalizeFirst(fallbackMessage),
            description: capitalizeFirst(errors[0].message),
            traceId,
            variant: 'destructive',
          });
        } else if (
          detailedMessage &&
          detailedMessage.toLowerCase() !== fallbackMessage.toLowerCase()
        ) {
          toast({
            title: capitalizeFirst(fallbackMessage),
            description: capitalizeFirst(detailedMessage),
            traceId,
            variant: 'destructive',
          });
        } else {
          toast({
            title: capitalizeFirst(detailedMessage || fallbackMessage),
            traceId,
            variant: 'destructive',
          });
        }
      }

      return Promise.reject(error);
    }

    // Handle 429 Too Many Requests
    if (status === 429) {
      if (!originalRequest?.skipGlobalErrorToast) {
        const traceId: string | undefined = responseData?.trace_id;
        const schemaMessage: string | undefined =
          responseData?.response_schema?.response_message;
        const fallbackMessage: string = schemaMessage || 'Too Many Requests';

        toast({
          title: capitalizeFirst(fallbackMessage),
          description:
            responseData?.message ||
            'Too many requests sent to the server. Please wait a moment and try again.',
          traceId,
          variant: 'destructive',
        });
      }

      return Promise.reject(error);
    }

    // Handle 500 Internal Server Error (or any 5xx server error / response_code 9999) — show toast with Trace ID
    if (
      (status && status >= 500) ||
      responseData?.response_schema?.response_code === '9999'
    ) {
      if (!originalRequest?.skipGlobalErrorToast) {
        const traceId: string | undefined = responseData?.trace_id;
        const schemaMessage: string | undefined =
          responseData?.response_schema?.response_message;
        const fallbackMessage: string =
          schemaMessage || 'Internal Server Error';

        toast({
          title: capitalizeFirst(fallbackMessage),
          description:
            responseData?.message ||
            'An unexpected server error occurred. Please try again or contact support if the problem persists.',
          traceId,
          variant: 'destructive',
        });
      }

      return Promise.reject(error);
    }

    // Fallback for other response errors if trace_id or error schema is present
    if (
      error.response &&
      !originalRequest?.skipGlobalErrorToast &&
      (responseData?.trace_id || responseData?.response_schema)
    ) {
      const traceId: string | undefined = responseData?.trace_id;
      const schemaMessage: string | undefined =
        responseData?.response_schema?.response_message;
      const fallbackMessage: string =
        schemaMessage ||
        responseData?.message ||
        `Request failed with status ${error.response.status}`;
      const detailedMessage = getApiErrorMessage(error, fallbackMessage);

      toast({
        title: capitalizeFirst(fallbackMessage),
        description:
          detailedMessage &&
          detailedMessage.toLowerCase() !== fallbackMessage.toLowerCase()
            ? capitalizeFirst(detailedMessage)
            : undefined,
        traceId,
        variant: 'destructive',
      });

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const forceRefreshToken = async (): Promise<string | null> => {
  try {
    const authStore = useAuthStore.getState();
    return authStore.refreshTokenIfNeeded();
  } catch {
    return null;
  }
};
